import { Box } from "@mui/material";
import { memo } from "react";
import { QUIZ } from "@/theme/quizTokens";

type MealMode = "FULL" | "MIXED";

type Props = {
    mealMode: MealMode;
    onToggle: (mode: MealMode) => void;
    /**
     * Plan-test arm "b": labels wrap instead of truncating ("60% Paws 40% Oth…")
     * and the mixed label reads "60% Paws, 40% other".
     */
    wrapLabels?: boolean;
};

const OPTIONS: { mode: MealMode; label: string }[] = [
    { mode: "FULL", label: "100% Paws on Plates" },
    { mode: "MIXED", label: "60% Paws 40% Other food" },
];

/** Segmented toggle (blue active) for FULL vs MIXED meal mode, sits inside the brown plan card. */
function MealModeToggle({ mealMode, onToggle, wrapLabels = false }: Props) {
    return (
        <Box
            sx={{
                width: "100%",
                background: QUIZ.color.white,
                borderRadius: QUIZ.radius.pill,
                p: "5px",
                display: "flex",
                mb: { xs: "16px", md: "24px" },
                gap: "4px",
            }}
        >
            {OPTIONS.map((opt) => {
                const active = mealMode === opt.mode;
                return (
                    <Box
                        key={opt.mode}
                        role="button"
                        onClick={() => onToggle(opt.mode)}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onToggle(opt.mode);
                        }}
                        sx={{
                            flex: 1,
                            textAlign: "center",
                            fontFamily: QUIZ.font.body,
                            fontWeight: 700,
                            fontSize: { xs: 12, md: 19 },
                            borderRadius: QUIZ.radius.pill,
                            py: { xs: "8px", md: "14px" },
                            px: "8px",
                            ...(wrapLabels
                                ? {
                                      whiteSpace: "normal",
                                      lineHeight: 1.2,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                  }
                                : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                            cursor: "pointer",
                            userSelect: "none",
                            touchAction: "manipulation",
                            background: active ? QUIZ.color.blue : "transparent",
                            color: active ? QUIZ.color.dark : QUIZ.color.grey,
                            transition: "background 0.2s ease, color 0.2s ease",
                        }}
                    >
                        {wrapLabels && opt.mode === "MIXED" ? "60% Paws, 40% other" : opt.label}
                    </Box>
                );
            })}
        </Box>
    );
}

export default memo(MealModeToggle);
